import { AgBytesAPI } from "@ai360/core";
import { useMemo } from "react";
import { createSelector } from "reselect";

import { getCustomerGuidForField, getSingleCustomerGuidForFields } from "~/customer-data/selectors";
import { computeIsSelectable } from "~/utils/products/use-product-list";
import { messages as globalMessages } from "~/i18n-messages";
import { useAppSelector } from "~/store/hooks";

const EMPTY_CROPS: AgBytesAPI.ICropSelectionListItem[] = [];

/** Whose crop list this is. `catalog` is the plain list: no linkage, no Agvance call. */
export type CropScope =
    | { kind: "customer"; customerGuid: string }
    | { kind: "location"; orgLevelGuid: string }
    | { kind: "catalog" };

export const CATALOG_SCOPE: CropScope = { kind: "catalog" };

/** A customer's scope; no customer -> `catalog`. */
export const customerScope = (customerGuid: string | null | undefined): CropScope =>
    customerGuid ? { kind: "customer", customerGuid } : CATALOG_SCOPE;

/** One distinct location -> that location; none or several -> `catalog` (databases can link one crop differently, so no union). */
export const locationScope = (orgLevelGuids: (string | null | undefined)[]): CropScope => {
    const distinct = Array.from(new Set(orgLevelGuids.filter(Boolean)));
    return distinct.length === 1 ? { kind: "location", orgLevelGuid: distinct[0] } : CATALOG_SCOPE;
};

export interface IUseCropListOptions {
    isEnabled?: boolean;
    scope: CropScope;
}

export interface IUseCropListResult {
    crops: AgBytesAPI.ICropSelectionListItem[];
    isFetching: boolean;
}

/** Builds every crop-list request so hooks, selectors and sagas share one cache entry. */
export const getCropListRequest = (scope: CropScope): AgBytesAPI.ICropSelectionListRequest => {
    switch (scope.kind) {
        case "customer":
            return { customerGuid: scope.customerGuid };
        case "location":
            return { orgLevelGuid: scope.orgLevelGuid };
        case "catalog":
            return { scope: AgBytesAPI.CropSelectionListScope.Catalog };
    }
};

const LINKED_REFETCH_SECONDS = 60;

/** Linked lists go stale (Agvance can change outside the app), so refetch on mount after 60s; `catalog` uses the cache. */
export const getRefetchOnMountOrArgChange = (scope: CropScope): number | undefined =>
    scope.kind === "catalog" ? undefined : LINKED_REFETCH_SECONDS;

/** For `mapStateToProps`: returns the same object while the customer is unchanged. */
export const getCustomerCropScope = createSelector(
    (customerGuid: string | null) => customerGuid,
    customerScope
);

/** Memoised on the customer string, so a fresh selector result doesn't re-issue the request. */
export const useCustomerCropScope = (customerGuid: string | null): CropScope =>
    useMemo(() => customerScope(customerGuid), [customerGuid]);

/** The field's customer if mapped, else `catalog` (e.g. a batch template). */
export const useFieldCropScope = (fieldGuid: string | null | undefined): CropScope =>
    useCustomerCropScope(useAppSelector((state) => getCustomerGuidForField(state, fieldGuid)));

/** Saga / selector twin of `useFieldsCropScope`. */
export const getFieldsCropScope = (
    state: Parameters<typeof getSingleCustomerGuidForFields>[0],
    fieldGuids: (string | null | undefined)[]
): CropScope => customerScope(getSingleCustomerGuidForFields(state, fieldGuids));

/** One distinct mapped customer across the fields, else `catalog`. */
export const useFieldsCropScope = (fieldGuids: (string | null | undefined)[]): CropScope =>
    useCustomerCropScope(
        useAppSelector((state) => getSingleCustomerGuidForFields(state, fieldGuids))
    );

/**
 * One RTK cache entry over `POST agbytes/v1.0/crop`. Use this instead of a per-surface
 * fetch, so identical arguments share one request.
 */
export const useCropList = ({
    isEnabled = true,
    scope,
}: IUseCropListOptions): IUseCropListResult => {
    const { data: crops = EMPTY_CROPS, isFetching } = AgBytesAPI.useGetCropSelectionListQuery(
        getCropListRequest(scope),
        { skip: !isEnabled, refetchOnMountOrArgChange: getRefetchOnMountOrArgChange(scope) }
    );
    return { crops, isFetching };
};

export interface ICropDropdownOption {
    activeYn: boolean;
    /** Linked rows with no purpose, kept per row (see `pickAgvanceSources`). Null unless linked. */
    agvanceNoPurposes: AgBytesAPI.ICropSelectionListItem["agvanceNoPurposes"];
    /** Full linked-purpose/chemistry detail -- steps 2-3 of the color ladder, plus Chemistry rescoping. Null unless linked. */
    agvancePurposes: AgBytesAPI.ICropSelectionListItem["agvancePurposes"];
    defaultMapColor: string;
    guid: string;
    id: string;
    isAgvanceLinked: boolean | null;
    name: string;
}

/**
 * Narrows a `useCropList()` result to the plain option shape crop consumers expect (the same shape
 * `AgBytesItem`/legacy `cropData` supplied: `guid`, `id`, `name`, `activeYn`, `defaultMapColor`), plus
 * `isAgvanceLinked` for visibility and `agvanceNoPurposes`/`agvancePurposes` for the color ladder and
 * crop/purpose-scoped Chemistry (`analysis-management-area-form.tsx`).
 */
export const toCropDropdownOptions = (
    crops: AgBytesAPI.ICropSelectionListItem[]
): ICropDropdownOption[] =>
    crops.map((crop) => ({
        activeYn: crop.activeYn,
        agvanceNoPurposes: crop.agvanceNoPurposes,
        agvancePurposes: crop.agvancePurposes,
        defaultMapColor: crop.defaultMapColor,
        guid: crop.cropGuid,
        id: String(crop.cropId),
        isAgvanceLinked: crop.isAgvanceLinked,
        name: crop.name,
    }));

/**
 * `mapStateToProps` equivalent of `useCropList()` + `toCropDropdownOptions()`, for the class
 * components that can't call the hook directly.
 */
const getCropListData = (
    state: unknown,
    customerGuid?: string | null
): AgBytesAPI.ICropSelectionListItem[] =>
    AgBytesAPI.extendedAi360Api.endpoints.getCropSelectionList.select(
        getCropListRequest(customerScope(customerGuid))
    )(state)?.data ?? EMPTY_CROPS;
export const getCropData = createSelector(getCropListData, toCropDropdownOptions);

/** `getCropData` for the customer the field belongs to (`Catalog` if unmapped). */
export const getCropDataForField = (
    state: Parameters<typeof getCustomerGuidForField>[0],
    fieldGuid: string | null | undefined
) => getCropData(state, getCustomerGuidForField(state, fieldGuid));

export interface ILinkageStampable {
    activeYn: boolean;
    isAgvanceLinked: boolean | null;
}

/**
 * Minimal shape these helpers actually call `formatMessage` with: one descriptor in, a plain
 * string out — narrower than `IntlShape["formatMessage"]` (which also takes values/opts and
 * can return `ReactNode[]`) so callers with a looser local prop type (e.g.
 * `RestrictedCropList`'s `(descriptor: unknown) => string`) stay assignable. Same
 * declare-what's-used move as `isProductUnlinked` in `blending-utils.tsx`; confirmed against
 * real call sites via `npm run typecheck:full`. Shared with `use-crop-purpose-list.ts`.
 */
export type FormatCropTooltipMessage = (descriptor: {
    id: string;
    defaultMessage: string;
}) => string;

/**
 * Stamps `isSelectable` (and, given `formatMessage`, an unlinked-crop `getToolTip`) onto any
 * option list carrying `activeYn`/`isAgvanceLinked` — not just raw crop rows, so a site with
 * its own already-shaped `{value, label, ...}` options (e.g. `RestrictedCropList`) can stamp
 * them too, not only the `toCropDropdownOptions()` pipeline below.
 */
export const stampCropSelectable = <T extends ILinkageStampable>(
    options: T[],
    formatMessage?: FormatCropTooltipMessage
): (T & { isSelectable: boolean; getToolTip?: () => string })[] =>
    options.map((option) => ({
        ...option,
        isSelectable: computeIsSelectable(option.activeYn, option.isAgvanceLinked),
        ...(formatMessage &&
            option.isAgvanceLinked === false && {
                getToolTip: () => formatMessage(globalMessages.cropNotLinkedInAgvance),
            }),
    }));

/**
 * Stamps `isSelectable` (and, given `formatMessage`, an unlinked-crop `getToolTip`) onto
 * `toCropDropdownOptions()`'s result so a `SelectInput` can key `optionIsHiddenKey` off it.
 */
export const toSelectableCropDropdownOptions = (
    crops: AgBytesAPI.ICropSelectionListItem[],
    formatMessage?: FormatCropTooltipMessage
): (ICropDropdownOption & { isSelectable: boolean; getToolTip?: () => string })[] =>
    stampCropSelectable(toCropDropdownOptions(crops), formatMessage);

export interface ICropSelectOption {
    activeYn: boolean;
    isAgvanceLinked: boolean | null;
    isSelectable: boolean;
    getToolTip?: () => string;
    label: string;
    value: string;
}

/**
 * The `{value, label}`-keyed counterpart to `toSelectableCropDropdownOptions()`, for
 * `SelectInput`-direct call sites (action-panel forms). Takes `ICropDropdownOption[]` (the
 * `guid`/`name`-keyed shape from `toCropDropdownOptions()`), not the raw
 * `AgBytesAPI.ICropSelectionListItem[]` — every action-panel form receives `cropData` already
 * converted by a parent (or `use-product-list.ts`'s class equivalent), never fetching raw
 * crops itself. Confirmed against real call sites (`event-planting-form.tsx`,
 * `analysis-profit-loss-form.tsx`, `event-scouting-template-modal.tsx`,
 * `batch-edit-events-modal.tsx`) via `npm run typecheck:full` — don't revert to the raw crop
 * type without reconfirming.
 *
 * `formatMessage` is required, not optional: every caller here wants a tooltip on an unlinked
 * crop, and making it optional would reintroduce the silent no-tooltip gap that
 * `toSelectableCropDropdownOptions` already has at most of its callers.
 */
export const toCropSelectOptions = (
    crops: ICropDropdownOption[],
    formatMessage: FormatCropTooltipMessage
): ICropSelectOption[] =>
    stampCropSelectable(
        crops.map((crop) => ({
            activeYn: crop.activeYn,
            isAgvanceLinked: crop.isAgvanceLinked,
            label: crop.name,
            value: crop.guid,
        })),
        formatMessage
    );

/**
 * Mirrors `blending-utils.tsx`'s `isProductUnlinked` -- a plain function, not a hook, so it can be
 * called from `mapStateToProps`, sagas, or a class `render()`, not just function components.
 */
export const isCropUnlinked = (
    cropGuid: string | null | undefined,
    crops: ICropDropdownOption[]
): boolean => {
    if (!cropGuid) {
        return false;
    }
    const crop = crops.find((c) => c.guid === cropGuid);
    return crop != null && crop.isAgvanceLinked === false;
};
